import { defineStore } from 'pinia'

import { myCookies, useMyFetch } from '@/utils'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

type Alert = {
  message: String
  type: String
}

export const useOrganizationStore = defineStore({
  id: 'organization',
  state: () => ({
    organization: JSON.parse(localStorage.getItem('organization') || '{}')
  }),
  getters: {
    hasOrganization: function (): boolean {
      return this.organization.id !== undefined
    }
  },
  actions: {
    currentOrganization: function () {
      return useMyFetch(`organizations/${this.organization.id}`).get().json()
    },
    selectOrganization: function (id: string, name: string) {
      this.organization = { id, name }
      localStorage.setItem('organization', JSON.stringify(this.organization))
    },
    clearOrganization: function () {
      this.organization = {}
      localStorage.removeItem('organization')
    }
  }
})

export const useAlertStore = defineStore({
  id: 'alert',
  state: () => ({
    alert: null as Alert | null
  }),
  actions: {
    success: function (message: String) {
      this.alert = { message, type: 'alert-success' }
    },
    error: function (message: String) {
      this.alert = { message, type: 'alert-danger' }
    },
    clear: function () {
      this.alert = null
    }
  }
})

export const useAuthStore = defineStore({
  id: 'auth',
  state: () => ({
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    returnUrl: '',
    cookies: myCookies
  }),
  actions: {
    async register(email: string, password: string) {
      if (!this.cookies.get('csrftoken')) {
        await useMyFetch('csrf')
      }

      return useMyFetch('register').post({ email, password }).json()
    },
    async login(email: string, password: string) {
      const alertStore = useAlertStore()

      if (!this.cookies.get('csrftoken')) {
        await useMyFetch('csrf')
      }

      const { data, error } = await useMyFetch('authenticate').post({ email, password }).json()
      if (error.value) {
        alertStore.error(error.value.message)
        return
      }
      this.user = data.value
      localStorage.setItem('user', JSON.stringify(this.user))
    },
    async logout() {
      await useMyFetch('logout').post().json()
      this.cookies.remove('sessionid')
      this.cookies.remove('csrftoken')
      this.user = null
      localStorage.removeItem('user')
    }
  }
})

export function useAllStores() {
  return {
    organizationStore: useOrganizationStore(),
    alertStore: useAlertStore(),
    authStore: useAuthStore(),
    router: useRouter(),
    route: useRoute(),
    t: useI18n().t
  }
}
